import {
  hasActiveEffects,
  shadowOffset,
  type LayerEffects,
  type StrokePosition,
} from '../core/layerStyles';
import { mat3Translation } from '../core/math';
import { GRADIENT_TYPE_IDS, gradientToLut } from '../core/gradient';
import { getPattern } from '../core/patterns';
import type { BlendMode, RGBA } from '../core/types';
import type { RenderTarget } from '../gl/renderTarget';
import type { Texture } from '../gl/texture';
import { gradientEndpoints } from './fillPass';
import type { Engine } from './engine';
import type { DocumentResources } from './resources';

const STROKE_POS: Record<StrokePosition, 0 | 1 | 2> = {
  outside: 0,
  inside: 1,
  center: 2,
};

function blurCoverage(
  engine: Engine,
  resources: DocumentResources,
  source: Texture,
  radius: number,
): RenderTarget {
  const { coverage } = resources.buffers;
  const a = coverage.acquire();
  const b = coverage.acquire();
  const sigma = Math.max(0.6, radius / 2);
  const taps = Math.max(1, Math.min(64, Math.ceil(radius * 1.5)));
  engine.renderer.blurPass(a.framebuffer, source, 1 / source.width, 0, taps, sigma);
  engine.renderer.blurPass(b.framebuffer, a.texture, 0, 1 / source.height, taps, sigma);
  coverage.release(a);
  return b;
}

function blendOnto(
  engine: Engine,
  resources: DocumentResources,
  backdrop: RenderTarget,
  layer: Texture,
  opacity: number,
  blendMode: BlendMode,
): RenderTarget {
  const next = resources.buffers.color.acquire();
  engine.renderer.blendLayer(next.framebuffer, backdrop.texture, {
    texture: layer,
    mask: null,
    opacity,
    blendMode,
  });
  resources.buffers.color.release(backdrop);
  return next;
}

function tintCoverage(
  engine: Engine,
  resources: DocumentResources,
  coverage: Texture,
  source: Texture,
  color: RGBA,
  opacity: number,
  clip: 0 | 1 | 2,
): RenderTarget {
  const target = resources.buffers.color.acquire();
  engine.renderer.fxTint(target.framebuffer, coverage, source, { color, opacity, clip });
  return target;
}

/**
 * Builds the pixels a layer contributes after its styles are applied. The
 * returned buffer is owned by the caller (pool colour target).
 */
export function applyLayerEffects(
  engine: Engine,
  resources: DocumentResources,
  source: Texture,
  effects: LayerEffects | null | undefined,
  width: number,
  height: number,
): { texture: Texture; owner: RenderTarget | null } {
  if (!hasActiveEffects(effects) || !effects) {
    return { texture: source, owner: null };
  }

  const { coverage, color } = resources.buffers;
  const alpha = coverage.acquire();
  engine.renderer.extractAlpha(alpha.framebuffer, source);

  let stack = color.acquire();
  stack.clear(0, 0, 0, 0);

  const drop = effects.dropShadow;
  if (drop?.enabled) {
    const { x, y } = shadowOffset(drop.angle, drop.distance);
    const offset = coverage.acquire();
    engine.renderer.sampleMask(offset.framebuffer, source, {
      srcMatrix: mat3Translation(-x / width, -y / height),
      fromAlpha: true,
    });
    const spread = Math.max(0, drop.spread) / 100;
    let silhouette = offset;
    if (spread > 0) {
      const expanded = blurCoverage(engine, resources, offset.texture, Math.max(1, drop.size * spread));
      coverage.release(offset);
      // Threshold soft expand toward a hard silhouette.
      const hard = coverage.acquire();
      engine.renderer.sampleMask(hard.framebuffer, expanded.texture, {
        fromAlpha: false,
      });
      coverage.release(expanded);
      silhouette = hard;
    }
    const blurred = blurCoverage(engine, resources, silhouette.texture, Math.max(0.1, drop.size));
    coverage.release(silhouette);
    const tinted = tintCoverage(engine, resources, blurred.texture, source, drop.color, 1, 0);
    coverage.release(blurred);
    stack = blendOnto(engine, resources, stack, tinted.texture, drop.opacity, drop.blendMode);
    color.release(tinted);
  }

  const glow = effects.outerGlow;
  if (glow?.enabled) {
    const blurred = blurCoverage(engine, resources, alpha.texture, Math.max(0.1, glow.size));
    const tinted = tintCoverage(engine, resources, blurred.texture, source, glow.color, 1, 1);
    coverage.release(blurred);
    stack = blendOnto(engine, resources, stack, tinted.texture, glow.opacity, glow.blendMode);
    color.release(tinted);
  }

  // Layer body sits above outer effects.
  stack = blendOnto(engine, resources, stack, source, 1, 'normal');

  const innerGlow = effects.innerGlow;
  if (innerGlow?.enabled) {
    const inverted = coverage.acquire();
    engine.renderer.sampleMask(inverted.framebuffer, source, { fromAlpha: true, invert: true });
    const blurred = blurCoverage(engine, resources, inverted.texture, Math.max(0.1, innerGlow.size));
    coverage.release(inverted);
    const tinted = tintCoverage(engine, resources, blurred.texture, source, innerGlow.color, 1, 2);
    coverage.release(blurred);
    stack = blendOnto(engine, resources, stack, tinted.texture, innerGlow.opacity, innerGlow.blendMode);
    color.release(tinted);
  }

  const inner = effects.innerShadow;
  if (inner?.enabled) {
    const { x, y } = shadowOffset(inner.angle, inner.distance);
    const offset = coverage.acquire();
    engine.renderer.sampleMask(offset.framebuffer, source, {
      srcMatrix: mat3Translation(-x / width, -y / height),
      fromAlpha: true,
      invert: true,
    });
    const blurred = blurCoverage(engine, resources, offset.texture, Math.max(0.1, inner.size));
    coverage.release(offset);
    const tinted = tintCoverage(engine, resources, blurred.texture, source, inner.color, 1, 2);
    coverage.release(blurred);
    stack = blendOnto(engine, resources, stack, tinted.texture, inner.opacity, inner.blendMode);
    color.release(tinted);
  }

  const bevel = effects.bevel;
  if (bevel?.enabled) {
    const soft =
      bevel.soften > 0 ? blurCoverage(engine, resources, alpha.texture, bevel.soften) : alpha;
    const rad = (bevel.angle * Math.PI) / 180;
    const elev = (bevel.altitude * Math.PI) / 180;
    const tinted = color.acquire();
    engine.renderer.fxBevel(tinted.framebuffer, soft.texture, source, {
      depth: Math.max(0.1, bevel.depth / 100) * Math.max(1, bevel.size),
      opacity: bevel.opacity,
      lightX: Math.cos(rad) * Math.cos(elev),
      lightY: -Math.sin(rad) * Math.cos(elev),
      highlight: bevel.highlightColor,
      highlightOpacity: bevel.highlightOpacity,
      shadow: bevel.shadowColor,
      shadowOpacity: bevel.shadowOpacity,
    });
    if (soft !== alpha) coverage.release(soft);
    stack = blendOnto(engine, resources, stack, tinted.texture, 1, bevel.blendMode);
    color.release(tinted);
  }

  const colorOverlay = effects.colorOverlay;
  if (colorOverlay?.enabled) {
    const overlay = color.acquire();
    overlay.clear(
      colorOverlay.color.r,
      colorOverlay.color.g,
      colorOverlay.color.b,
      colorOverlay.color.a,
    );
    const clipped = color.acquire();
    engine.renderer.fxOverlay(clipped.framebuffer, source, overlay.texture, 1);
    color.release(overlay);
    stack = blendOnto(
      engine,
      resources,
      stack,
      clipped.texture,
      colorOverlay.opacity,
      colorOverlay.blendMode,
    );
    color.release(clipped);
  }

  const gradientOverlay = effects.gradientOverlay;
  if (gradientOverlay?.enabled) {
    const empty = color.acquire();
    empty.clear(0, 0, 0, 0);
    const painted = color.acquire();
    const lutKey = `fx-grad:${JSON.stringify(gradientOverlay.gradient)}`;
    const lut = engine.lutTexture(lutKey, () => gradientToLut(gradientOverlay.gradient));
    if (lut) {
      const { start, end } = gradientEndpoints(
        width,
        height,
        gradientOverlay.angle,
        gradientOverlay.scale,
      );
      engine.renderer.drawGradient(painted.framebuffer, empty.texture, null, {
        type: GRADIENT_TYPE_IDS.linear,
        start,
        end,
        lut,
        opacity: 1,
        blendMode: 'normal',
        reverse: gradientOverlay.reverse,
        dither: true,
      });
    } else {
      painted.clear(0, 0, 0, 0);
    }
    color.release(empty);
    const clipped = color.acquire();
    engine.renderer.fxOverlay(clipped.framebuffer, source, painted.texture, 1);
    color.release(painted);
    stack = blendOnto(
      engine,
      resources,
      stack,
      clipped.texture,
      gradientOverlay.opacity,
      gradientOverlay.blendMode,
    );
    color.release(clipped);
  }

  const patternOverlay = effects.patternOverlay;
  if (patternOverlay?.enabled) {
    const pattern = getPattern(patternOverlay.patternId);
    const tile =
      pattern && engine.patternTexture(pattern.id, pattern.width, pattern.height, pattern.data);
    if (pattern && tile) {
      const empty = color.acquire();
      empty.clear(0, 0, 0, 0);
      const painted = color.acquire();
      const scale = Math.max(1, patternOverlay.scale) / 100;
      engine.renderer.applyPattern(
        painted.framebuffer,
        empty.texture,
        engine.renderer.whiteMaskTexture,
        tile,
        {
          docWidth: width,
          docHeight: height,
          originX: 0,
          originY: 0,
          opacity: 1,
          blendMode: 'normal',
          tileWidth: pattern.width * scale,
          tileHeight: pattern.height * scale,
        },
      );
      color.release(empty);
      const clipped = color.acquire();
      engine.renderer.fxOverlay(clipped.framebuffer, source, painted.texture, 1);
      color.release(painted);
      stack = blendOnto(
        engine,
        resources,
        stack,
        clipped.texture,
        patternOverlay.opacity,
        patternOverlay.blendMode,
      );
      color.release(clipped);
    }
  }

  const stroke = effects.stroke;
  if (stroke?.enabled && stroke.size > 0) {
    const blurred = blurCoverage(engine, resources, alpha.texture, Math.max(0.5, stroke.size));
    const ring = color.acquire();
    engine.renderer.fxStroke(ring.framebuffer, alpha.texture, blurred.texture, {
      color: stroke.color,
      opacity: 1,
      position: STROKE_POS[stroke.position],
      soft: 0.15,
    });
    coverage.release(blurred);
    stack = blendOnto(engine, resources, stack, ring.texture, stroke.opacity, stroke.blendMode);
    color.release(ring);
  }

  coverage.release(alpha);
  return { texture: stack.texture, owner: stack };
}
